/**
 * Central PII capture: normalizes raw form/API input per the platform variant
 * contract (pii-normalize.ts), hashes with Web Crypto, and stores the variants
 * in state.userData. Every capture path (form listeners, AJAX detection,
 * setUserData) goes through here so normalization can never drift per caller.
 *
 * Raw values additionally feed browser-side enhanced conversions (Microsoft
 * UET pid, Google gtag user_data) in-memory at capture time: those tags want
 * RAW input and normalize+hash themselves. The raw value is never stored and
 * never sent to our own server on this branch (v3's identity layer will).
 */

import { state, debugLog } from "./state.js";
import { sha256 } from "./hashing.js";
import { isMarketingAllowed } from "./consent.js";
import {
  normalizeEmail,
  normalizeEmailGoogle,
  normalizeEmailReddit,
  normalizeEmailStrict,
  normalizePhoneDigits,
  normalizePhoneE164,
  normalizeName,
  normalizeNameOpenAI,
  normalizeCity,
  normalizeState,
  normalizeZip,
  normalizeCountry,
  normalizeGender,
  normalizeBirthday,
} from "./pii-normalize.js";
import { dialCodeForCountry } from "./dial-codes.js";
import { persistUserData } from "./user-data-store.js";

/** Hash a normalized value into state.userData under the given key. */
function hashInto(key: string, normalized: string | null): Promise<void> {
  if (!normalized) return Promise.resolve();
  return sha256(normalized).then((hash) => {
    if (hash) state.userData[key] = hash;
  });
}

/** Dial code for the visitor's country (GeoIP, served in /events/config). */
function visitorDialCode(): string | null {
  return dialCodeForCountry(state.config?.visitorCountry);
}

/**
 * Push raw identifiers to tags that do their own normalization + hashing
 * (Microsoft UET enhanced conversions, Google enhanced conversions).
 * In-memory only; gated on marketing consent like the pixels themselves.
 */
function pushRawEnhancedPii(rawEmail: string | null, phoneE164: string | null): void {
  if (!rawEmail && !phoneE164) return;
  if (!isMarketingAllowed()) return;

  const platforms = state.config?.platforms;

  try {
    if (window.uetq && platforms?.bing_ads) {
      const pid: Record<string, string> = {};
      if (rawEmail) pid.em = rawEmail;
      if (phoneE164) pid.ph = phoneE164;
      window.uetq.push("set", { pid });
      debugLog("piiCapture", "UET pid set");
    }
  } catch { /* UET unavailable, continue */ }

  try {
    const wantsGoogle = platforms?.google_ads || platforms?.ga4;
    if (window.gtag && wantsGoogle) {
      const userData: Record<string, string> = {};
      if (rawEmail) userData.email = rawEmail;
      if (phoneE164) userData.phone_number = phoneE164;
      state.selfDispatch = true;
      window.gtag("set", "user_data", userData);
      state.selfDispatch = false;
      debugLog("piiCapture", "gtag user_data set");
    }
  } catch { /* gtag unavailable, continue */ }
}

/** URL param for e-mail-link tracking: ESPs fill it with a merge tag so a
 * newsletter click identifies the visitor from the very first pageview. */
const EMAIL_LINK_PARAM = "tst_em";
const SHA256_HEX_RE = /^[a-f0-9]{64}$/i;

/**
 * Capture the e-mail identity from a ?tst_em= landing param. Accepts a raw
 * address (variant hashes are derived) or a pre-computed SHA-256 hash
 * (stored as em only; variants cannot be derived from a hash). The
 * transport ALWAYS strips this param from the reported page URL.
 */
export function captureEmailLinkParam(): Promise<void> {
  try {
    const value = new URLSearchParams(window.location.search).get(EMAIL_LINK_PARAM);
    if (!value) return Promise.resolve();
    if (SHA256_HEX_RE.test(value)) {
      state.userData.em = value.toLowerCase();
      persistUserData();
      return Promise.resolve();
    }
    if (value.includes("@")) {
      // captureEmail only fills the in-memory hashes; persist so the next
      // pageload keeps the identity
      return captureEmail(value).then(() => persistUserData());
    }
    return Promise.resolve();
  } catch {
    return Promise.resolve();
  }
}

/** Capture an email address: hashes em / em_g / em_r / em_s variants. */
export function captureEmail(raw: string): Promise<void> {
  const base = normalizeEmail(raw);
  if (!base) return Promise.resolve();

  const google = normalizeEmailGoogle(raw);
  const reddit = normalizeEmailReddit(raw);
  const strict = normalizeEmailStrict(raw);

  // A new email replaces the whole identity: stale variant hashes from a
  // PREVIOUS address must not survive next to the fresh base hash (a Google
  // upload with an old em_g would carry someone else's identity)
  delete state.userData.em_g;
  delete state.userData.em_r;
  delete state.userData.em_s;

  const promises = [hashInto("em", base)];
  // Variants only when they differ; adapters fall back to em
  if (google && google !== base) promises.push(hashInto("em_g", google));
  if (reddit && reddit !== base) promises.push(hashInto("em_r", reddit));
  if (strict && strict !== base) promises.push(hashInto("em_s", strict));

  pushRawEnhancedPii(base, null);
  return Promise.all(promises).then(() => undefined);
}

/** Capture a phone number: hashes ph (digits) / ph_e (E.164) variants. */
export function capturePhone(raw: string): Promise<void> {
  const dial = visitorDialCode();
  const digits = normalizePhoneDigits(raw, dial);
  const e164 = normalizePhoneE164(raw, dial);
  if (!digits) {
    debugLog("piiCapture", "phone dropped: no resolvable country/format");
    return Promise.resolve();
  }

  // A new (valid) number replaces the previous one entirely
  delete state.userData.ph;
  delete state.userData.ph_e;

  pushRawEnhancedPii(null, e164);
  return Promise.all([hashInto("ph", digits), hashInto("ph_e", e164)]).then(() => undefined);
}

/** Capture first and/or last name (normalized: lowercase, no punctuation). */
export function captureNamePair(first: string | null, last: string | null): Promise<void> {
  const promises: Promise<void>[] = [];
  // Same hygiene as the email variants: a stale spaceless variant from a
  // previous person on this browser must never ride along with a new name.
  delete state.userData.fn_o;
  delete state.userData.ln_o;
  if (first) {
    promises.push(hashInto("fn", normalizeName(first)));
    const firstOpenAI = normalizeNameOpenAI(first);
    if (firstOpenAI && firstOpenAI !== normalizeName(first)) {
      promises.push(hashInto("fn_o", firstOpenAI));
    }
  }
  if (last) {
    promises.push(hashInto("ln", normalizeName(last)));
    const lastOpenAI = normalizeNameOpenAI(last);
    if (lastOpenAI && lastOpenAI !== normalizeName(last)) {
      promises.push(hashInto("ln_o", lastOpenAI));
    }
  }

  // LinkedIn-only opt-in (connector toggle): plaintext names for LinkedIn's
  // probabilistic userInfo matching; the API requires first AND last together
  if (state.config?.platforms?.linkedin?.collectPlaintextNames && first && last) {
    state.userData.li_fn = String(first).trim();
    state.userData.li_ln = String(last).trim();
  }

  return Promise.all(promises).then(() => undefined);
}

/** Capture a single full-name field: split on the first space into fn + ln. */
export function captureFullName(raw: string): Promise<void> {
  const fullName = String(raw).trim();
  if (!fullName) return Promise.resolve();
  const spaceIdx = fullName.indexOf(" ");
  if (spaceIdx > 0) {
    return captureNamePair(fullName.substring(0, spaceIdx), fullName.substring(spaceIdx + 1));
  }
  return captureNamePair(fullName, null);
}

/** Normalizers for the setUserData profile fields, keyed by public API name. */
const PROFILE_FIELDS: Record<string, { key: string; normalize: (raw: string) => string | null }> = {
  city: { key: "ct", normalize: normalizeCity },
  state: { key: "st", normalize: normalizeState },
  zip: { key: "zp", normalize: normalizeZip },
  // Alias: the webhook surface calls this field postCode; accept both so
  // setUserData callers cannot silently lose the zip hash
  postCode: { key: "zp", normalize: normalizeZip },
  country: { key: "country", normalize: normalizeCountry },
  gender: { key: "ge", normalize: normalizeGender },
  birthday: { key: "db", normalize: normalizeBirthday },
};

/**
 * Capture the full setUserData payload: email/phone/name via the variant
 * paths, profile fields via their normalizers, externalId raw.
 */
export function captureUserDataFields(data: Record<string, string>): Promise<void> {
  const promises: Promise<void>[] = [];

  if (data.email) promises.push(captureEmail(data.email));
  if (data.phone) promises.push(capturePhone(data.phone));
  if (data.firstName || data.lastName) {
    promises.push(captureNamePair(data.firstName || null, data.lastName || null));
  }

  for (const [field, config] of Object.entries(PROFILE_FIELDS)) {
    if (data[field]) promises.push(hashInto(config.key, config.normalize(data[field])));
  }

  if (data.externalId) state.userData.external_id = data.externalId;

  /**
   * The account id of the shop or app itself, hashed like every other
   * identifier before it leaves the page.
   *
   * Its own field, deliberately, and not folded into `externalId`: that one
   * means "the browser we handed this cookie to" everywhere it is read, while
   * this means "the human who is logged in", which is a stronger claim and
   * survives a new browser, a cleared cookie and a second device.
   *
   * An address here is a mistake worth refusing rather than storing: it would
   * be hashed as an opaque id and never match the e-mail hashes the platforms
   * expect, so it would look like it worked and quietly match nothing.
   */
  if (data.userId) {
    if (data.userId.indexOf("@") >= 0) {
      debugLog("setUserData", "userId looks like an e-mail address; use email instead");
    } else {
      promises.push(hashInto("user_id", data.userId.trim()));
    }
  }

  return Promise.all(promises).then(() => undefined);
}
